import {
	type CreateCheckoutSessionInput,
	CreateCheckoutSessionSchema,
} from "@gosaas/schemas";
import { hasPermission, type Permission, type Role } from "@gosaas/types/rbac";
import { getPlanByProductId, stripePlans } from "@gosaas/types/stripe";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconCurrencyDollar } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import { InvoiceHistoryCard } from "@/components/billing/invoice-history";
import { PlanCheckoutConfigurator } from "@/components/billing/plan-checkout-configurator";
import {
	billingDetailsQueryKey,
	fetchBillingDetailsData,
	useBillingDetails,
} from "@/components/billing/use-billing-details";
import { BillingFaqs } from "@/components/faqs/billing";
import { Loader } from "@/components/Loader";
import { PageHeader } from "@/components/page-header";
import { PageLayout } from "@/components/page-layout";
import { useTeam } from "@/components/providers/team";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { PricingCard, PricingTable } from "@/components/ui/pricing-table";
import { config } from "@/data/config";
import {
	isBillingApiPricingEnabled,
	isBillingFreeTrialEnabled,
} from "@/lib/modules";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";
import { QUERY_ADDON_TIERS_FALLBACK } from "@/utils/api-pricing";
import { StripePlansToProducts } from "@/utils/billing";
import {
	fetchApiQueryAddons,
	removeApiQueryAddon,
	setApiQueryAddon,
} from "@/utils/billing-api";
import {
	checkoutAddonPriceId,
	defaultCheckoutProductId,
	managedAddonApplyLabel,
	managedAddonIsUnchanged,
	managedAddonSliderIndex,
	subscriptionBillingIsAnnual,
} from "@/utils/plan-checkout";

const billingSearchSchema = z.object({
	success: z.boolean().optional(),
	cancel: z.boolean().optional(),
	return: z.enum(["portal"]).optional(),
});

type CheckoutSessionData = {
	checkout_session_id: string;
	url: string;
};

export function resolveCheckoutCustomerId(
	data: CreateCheckoutSessionInput,
	teamCustomerId?: string,
) {
	if (!data.customerID && teamCustomerId) {
		data.customerID = teamCustomerId;
	}
	return data;
}

export function pricingCardButtonLabel(
	isCurrentPlan: boolean,
	isActive: boolean,
	isSelected = false,
	selectionMode = false,
): string {
	if (selectionMode && !isActive) {
		if (isSelected) {
			return "Selected";
		}
		return "Select plan";
	}
	if (isCurrentPlan) {
		return "Current Plan";
	}
	if (isActive) {
		return "Manage Subscription";
	}
	return "Choose Plan";
}

export function isPricingCardDisabled(
	billingUpdate: boolean,
	isCurrentPlan: boolean,
	_teamType: string | undefined,
	_productName: string,
): boolean {
	return !billingUpdate || isCurrentPlan;
}

export const Route = createFileRoute("/_authorized/team/billing/")({
	component: Billing,
	validateSearch: (search: Record<string, unknown>) => {
		const normalizedSearch = normalizeBillingSearchParams(search);
		// Use safeParse to avoid throwing 500 errors on invalid search params
		const result = billingSearchSchema.safeParse(normalizedSearch);
		if (result.success) {
			return result.data;
		}
		// Return default values if validation fails
		return { success: undefined, cancel: undefined, return: undefined };
	},
});

export function normalizeBillingSearchParams(
	search: Record<string, unknown>,
): Record<string, unknown> {
	return {
		...search,
		success:
			typeof search.success === "string"
				? search.success === "true"
				: search.success,
		cancel:
			typeof search.cancel === "string"
				? search.cancel === "true"
				: search.cancel,
		return: search.return === "portal" ? "portal" : undefined,
	};
}

export function isBillingCheckoutSuccessRedirect(success?: boolean): boolean {
	return Boolean(success);
}

export function isBillingCheckoutCancelRedirect(cancel?: boolean): boolean {
	return Boolean(cancel);
}

export function isBillingPortalReturnRedirect(returnTo?: "portal"): boolean {
	return returnTo === "portal";
}

export function billingFormDefaultsFromCustomerId(customerId?: string | null) {
	if (!customerId) {
		return null;
	}

	return {
		customerID: customerId,
		priceID: "",
		productID: "",
	};
}

export function isBillingNotFoundError(error: unknown): boolean {
	return (
		error instanceof TypedClientApiError && error.code === "BILLING_NOT_FOUND"
	);
}

export function billingApiErrorDescription(error: unknown): string {
	return error instanceof TypedClientApiError
		? error.message
		: "An unknown error occurred";
}

export function shouldLogBillingDevMessage(isDev: boolean): boolean {
	return isDev;
}

export function logBillingNotFoundInDev(isDev: boolean): void {
	if (shouldLogBillingDevMessage(isDev)) {
		console.warn("Billing not found for team");
	}
}

export function logMissingCheckoutDataInDev(isDev: boolean): void {
	if (shouldLogBillingDevMessage(isDev)) {
		console.error("Missing data for checkout");
	}
}

export function logMissingBillingPlanInDev(isDev: boolean): void {
	if (shouldLogBillingDevMessage(isDev)) {
		console.error("Plan not found for productId");
	}
}

export function selectBillingPlanPriceId(
	isAnnual: boolean,
	plan: { yearlyPrice?: { id: string }; monthlyPrice: { id: string } },
): string {
	return isAnnual && plan.yearlyPrice
		? plan.yearlyPrice.id
		: plan.monthlyPrice.id;
}

export function subscriptionPeriodEndLabel(cancelAtPeriodEnd: boolean): string {
	return cancelAtPeriodEnd ? "Access until" : "Next Billing Date";
}

export function subscriptionCancellationNotice(periodEnd: string): string {
	const formattedDate = new Date(periodEnd).toLocaleDateString();
	return `Your subscription is set to cancel and won't renew. You'll keep access until ${formattedDate}.`;
}

export async function fetchBillingPortalURL(teamID: string): Promise<string> {
	const response = await requestWithHandling<{
		manage_subscription_url?: string;
	}>(() => apiClient.api.billingPortalCreate(teamID));
	if (response.code !== "SUCCESS" || !response.data?.manage_subscription_url) {
		throw new Error(
			response.message || "Manage subscription URL not available.",
		);
	}
	return response.data.manage_subscription_url;
}

export function Billing() {
	const router = useRouter();
	const queryClient = useQueryClient();
	const { teams, teamID, loading, refresh } = useTeam();
	const team = teams.find((t) => t.id === teamID);
	const searchParams = useSearch({ from: Route.id });

	const isStripeRedirect =
		isBillingCheckoutSuccessRedirect(searchParams.success) ||
		isBillingPortalReturnRedirect(searchParams.return) ||
		isBillingCheckoutCancelRedirect(searchParams.cancel);

	const {
		data: billingDetails,
		error: billingDetailsError,
		isError: isBillingDetailsError,
	} = useBillingDetails(teamID, {
		enabled: !!teamID && !isStripeRedirect,
	});
	const [isAnnual, setIsAnnual] = useState(false);
	const [isAddonUpdating, setIsAddonUpdating] = useState(false);
	const [selectedProductId, setSelectedProductId] = useState<string | null>(
		null,
	);
	const [addonIndex, setAddonIndex] = useState(0);
	const [manageAddonIndex, setManageAddonIndex] = useState(0);
	const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

	const apiPricingEnabled = isBillingApiPricingEnabled();
	const { data: queryAddonTiers = QUERY_ADDON_TIERS_FALLBACK } = useQuery({
		queryKey: ["billing-query-addons"],
		queryFn: fetchApiQueryAddons,
		enabled: apiPricingEnabled,
		staleTime: 60_000,
		placeholderData: QUERY_ADDON_TIERS_FALLBACK,
	});

	const billingUpdate = hasPermission(
		team?.role as Role,
		"billing:update" as Permission,
	);

	const billingRead = hasPermission(
		team?.role as Role,
		"billing:read" as Permission,
	);

	useEffect(() => {
		if (team?.email === "") {
			toast.warning("Add a team email before managing billing", {
				description:
					"Please have a team owner or admin set the team email address before managing billing.",
			});
			router.navigate({ to: "/team/settings" });
		}
	}, [team?.email, router]);

	useEffect(() => {
		if (!billingRead) {
			toast.warning("You do not have permission to manage billing", {
				description: "Please contact support if you believe this is an error.",
			});
			router.navigate({ to: "/team/settings" });
		}
	}, [billingRead, router]);

	const { setValue, reset, getValues } = useForm<CreateCheckoutSessionInput>({
		resolver: zodResolver(CreateCheckoutSessionSchema),
		defaultValues: {
			customerID: "",
			priceID: "",
			productID: "",
		},
	});

	const fetchBillingDetails = async (options?: {
		refreshTeam?: boolean;
		sync?: boolean;
	}) => {
		if (!teamID) return;
		try {
			const data = await fetchBillingDetailsData(teamID, options?.sync);
			queryClient.setQueryData(billingDetailsQueryKey(teamID), data);
			if (options?.refreshTeam) {
				await refresh();
			}
		} catch (error) {
			if (isBillingNotFoundError(error)) {
				logBillingNotFoundInDev(import.meta.env.DEV);
			} else if (
				!(error instanceof TypedClientApiError) ||
				error.code !== "RATE_LIMIT_EXCEEDED"
			) {
				toast.error("Couldn't load your billing details", {
					description: billingApiErrorDescription(error),
				});
			}
		}
	};

	useEffect(() => {
		if (!isBillingDetailsError || !billingDetailsError) {
			return;
		}
		if (isBillingNotFoundError(billingDetailsError)) {
			logBillingNotFoundInDev(import.meta.env.DEV);
			return;
		}
		if (
			billingDetailsError instanceof TypedClientApiError &&
			billingDetailsError.code === "RATE_LIMIT_EXCEEDED"
		) {
			return;
		}
		toast.error("Couldn't load your billing details", {
			description: billingApiErrorDescription(billingDetailsError),
		});
	}, [isBillingDetailsError, billingDetailsError]);

	const openBillingPortal = useCallback(async () => {
		if (!teamID) return;
		try {
			const portalURL = await fetchBillingPortalURL(teamID);
			window.location.href = portalURL;
		} catch (error) {
			toast.error("Manage subscription URL not available.", {
				description: billingApiErrorDescription(error),
			});
		}
	}, [teamID]);

	const reconcileAfterStripeRedirect = async (message: string) => {
		toast.success(message);
		router.navigate({ to: "/team/billing", replace: true });
		if (teamID) {
			try {
				await requestWithHandling(() =>
					apiClient.api.billingSyncStripeCreate(teamID),
				);
			} catch (error) {
				console.error("Failed to sync billing from Stripe", error);
			}
			await fetchBillingDetails({ refreshTeam: true, sync: true });
			await queryClient.invalidateQueries({
				queryKey: ["billing-invoices", teamID],
			});
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: run once per team or Stripe redirect
	useEffect(() => {
		if (!teamID) return;
		if (isBillingCheckoutSuccessRedirect(searchParams.success)) {
			void reconcileAfterStripeRedirect("Subscription updated successfully!");
			return;
		}
		if (isBillingPortalReturnRedirect(searchParams.return)) {
			void reconcileAfterStripeRedirect("Subscription details updated.");
			return;
		}
		if (isBillingCheckoutCancelRedirect(searchParams.cancel)) {
			toast.info("Checkout canceled", {
				description: "No changes were made to your subscription.",
			});
			router.navigate({ to: "/team/billing", replace: true });
		}
	}, [teamID, searchParams.success, searchParams.return, searchParams.cancel]);

	useEffect(() => {
		const defaults = billingFormDefaultsFromCustomerId(
			team?.billing?.customer_id,
		);
		if (defaults) {
			reset(defaults);
		}
	}, [team, reset]);

	const isSubscribed = Boolean(team?.billing?.is_active);

	useEffect(() => {
		if (!apiPricingEnabled || isSubscribed || selectedProductId || !team) {
			return;
		}
		const defaultProductId = defaultCheckoutProductId(team.type);
		if (defaultProductId) {
			setSelectedProductId(defaultProductId);
		}
	}, [apiPricingEnabled, isSubscribed, selectedProductId, team]);

	useEffect(() => {
		if (!isSubscribed || queryAddonTiers.length === 0) {
			return;
		}
		const addonQueries = billingDetails?.api_pricing?.addon_api_requests ?? 0;
		setManageAddonIndex(managedAddonSliderIndex(addonQueries, queryAddonTiers));
	}, [
		billingDetails?.api_pricing?.addon_api_requests,
		isSubscribed,
		queryAddonTiers,
	]);

	if (team?.email === "" || !billingRead) {
		return null;
	}

	if (loading || !team) {
		return <Loader />;
	}

	const onSubmit = async (data: CreateCheckoutSessionInput) => {
		if (
			!team?.billing?.is_active &&
			!billingDetails &&
			!team?.billing?.customer_id
		) {
			toast.error("Billing details aren't ready yet", {
				description: "Please wait a moment and try again.",
			});
			return;
		}

		if (team?.billing?.is_active) {
			toast.warning("You are already subscribed to a plan", {
				description:
					"You can manage your subscription by clicking the button below.",
			});
			void openBillingPortal();
			return;
		}

		resolveCheckoutCustomerId(data, team?.billing?.customer_id);

		if (!data.priceID || !data.productID || !data.customerID) {
			toast.error("Missing required information", {
				description: "Please select a plan.",
			});
			logMissingCheckoutDataInDev(import.meta.env.DEV);
			return;
		}

		try {
			const checkoutBody: {
				customer_id: string;
				price_id: string;
				product_id: string;
				addon_price_id?: string;
			} = {
				customer_id: data.customerID,
				price_id: data.priceID,
				product_id: data.productID,
			};
			if (data.addonPriceID) {
				checkoutBody.addon_price_id = data.addonPriceID;
			}

			const response = await requestWithHandling<CheckoutSessionData>(() =>
				apiClient.api.billingCheckoutCreate(teamID as string, checkoutBody),
			);
			const checkout = response.data;
			if (response.code !== "SUCCESS" || !checkout?.url) {
				throw new Error(response.message || "Failed to get checkout URL");
			}
			window.location.href = checkout.url;
		} catch (error) {
			toast.error("Failed to create checkout session", {
				description: billingApiErrorDescription(error),
			});
		} finally {
			setIsCheckoutLoading(false);
		}
	};

	const handleSelectAddon = async (priceId: string) => {
		if (!teamID) return;
		setIsAddonUpdating(true);
		try {
			await setApiQueryAddon(teamID, priceId);
			await fetchBillingDetails({ refreshTeam: true, sync: true });
			toast.success("API query add-on updated");
		} catch (error) {
			toast.error("Failed to update add-on", {
				description: billingApiErrorDescription(error),
			});
		} finally {
			setIsAddonUpdating(false);
		}
	};

	const handleRemoveAddon = async () => {
		if (!teamID) return;
		setIsAddonUpdating(true);
		try {
			await removeApiQueryAddon(teamID);
			await fetchBillingDetails({ refreshTeam: true, sync: true });
			toast.success("API query add-on removed");
		} catch (error) {
			toast.error("Failed to remove add-on", {
				description: billingApiErrorDescription(error),
			});
		} finally {
			setIsAddonUpdating(false);
		}
	};

	const handleApplyManagedAddon = async () => {
		if (!billingUpdate) return;

		if (manageAddonIndex <= 0) {
			if ((billingDetails?.api_pricing?.addon_api_requests ?? 0) > 0) {
				await handleRemoveAddon();
			}
			return;
		}

		const priceId = checkoutAddonPriceId(
			manageAddonIndex,
			queryAddonTiers,
			subscriptionIsAnnual,
		);
		if (!priceId) {
			toast.error("Couldn't load add-on pricing");
			return;
		}
		await handleSelectAddon(priceId);
	};

	const handleChoosePlan = (productId: string) => {
		const plan = getPlanByProductId(productId);
		if (!plan) {
			toast.error("Couldn't load plan details");
			logMissingBillingPlanInDev(import.meta.env.DEV);
			return;
		}

		const priceId = selectBillingPlanPriceId(isAnnual, plan);

		setValue("productID", productId);
		setValue("priceID", priceId);
		setIsCheckoutLoading(true);
		void onSubmit({
			customerID: getValues("customerID") || team?.billing?.customer_id || "",
			productID: productId,
			priceID: priceId,
		});
	};

	const handleSelectPlan = (productId: string) => {
		if (team?.billing?.is_active) {
			handleChoosePlan(productId);
			return;
		}
		if (!apiPricingEnabled) {
			handleChoosePlan(productId);
			return;
		}
		setSelectedProductId(productId);
		setAddonIndex(0);
	};

	const handleContinueCheckout = async () => {
		if (!selectedProductId) {
			toast.error("Select a plan to continue", {
				description: "Choose the tier that fits your team before checkout.",
			});
			return;
		}

		const plan = getPlanByProductId(selectedProductId);
		if (!plan) {
			toast.error("Couldn't load plan details");
			logMissingBillingPlanInDev(import.meta.env.DEV);
			return;
		}

		const priceId = selectBillingPlanPriceId(isAnnual, plan);
		const addonPriceId =
			apiPricingEnabled && checkoutTiers.length > 0
				? checkoutAddonPriceId(addonIndex, checkoutTiers, isAnnual)
				: undefined;

		setValue("productID", selectedProductId);
		setValue("priceID", priceId);
		if (addonPriceId) {
			setValue("addonPriceID", addonPriceId);
		}

		setIsCheckoutLoading(true);
		await onSubmit({
			customerID: getValues("customerID") || team?.billing?.customer_id || "",
			productID: selectedProductId,
			priceID: priceId,
			addonPriceID: addonPriceId,
		});
	};

	const activePlan = isSubscribed
		? stripePlans.find((p) => p.productId === team.billing?.stripe_product_id)
		: undefined;
	const subscriptionIsAnnual = subscriptionBillingIsAnnual(
		activePlan,
		team.billing?.stripe_price_id,
	);
	const currentAddonQueries =
		billingDetails?.api_pricing?.addon_api_requests ?? 0;
	const currentAddonPriceId =
		billingDetails?.api_pricing?.stripe_addon_price_id;
	const selectedPlan = selectedProductId
		? stripePlans.find((p) => p.productId === selectedProductId)
		: undefined;
	const checkoutTiers = apiPricingEnabled ? queryAddonTiers : [];
	const managedAddonUnchanged = managedAddonIsUnchanged(
		manageAddonIndex,
		queryAddonTiers,
		subscriptionIsAnnual,
		currentAddonQueries,
		currentAddonPriceId,
	);

	const BillingContent = ({
		isAnnual,
		setIsAnnual,
	}: {
		isAnnual: boolean;
		setIsAnnual: (isAnnual: boolean) => void;
	}) => {
		const products = StripePlansToProducts;

		return (
			<PricingTable
				products={products}
				showFeatures={true}
				isAnnual={isAnnual}
				setIsAnnual={setIsAnnual}
			>
				{products.map((product) => {
					const plan = stripePlans.find((p) => p.productId === product.id);
					if (!plan) return null;

					const isCurrentPlan =
						team?.billing?.stripe_product_id === product.id &&
						team?.billing?.is_active;
					const isDisabled = isPricingCardDisabled(
						billingUpdate,
						isCurrentPlan,
						team?.type,
						product.name,
					);

					const isSelected =
						apiPricingEnabled &&
						!isSubscribed &&
						selectedProductId === product.id;

					return (
						<PricingCard
							key={product.id}
							productId={product.id}
							isSelected={isSelected}
							onSelect={
								apiPricingEnabled && !isSubscribed && !isDisabled
									? () => {
											setSelectedProductId(product.id);
											setAddonIndex(0);
										}
									: undefined
							}
							onButtonClick={() => {
								handleSelectPlan(product.id);
							}}
							buttonProps={{
								disabled: isDisabled || isCheckoutLoading,
								children: pricingCardButtonLabel(
									isCurrentPlan,
									isSubscribed,
									isSelected,
									apiPricingEnabled && !isSubscribed,
								),
							}}
						/>
					);
				})}
			</PricingTable>
		);
	};

	return (
		<PageLayout>
			<PageHeader
				breadcrumbItems={[
					{ title: "Team", href: null },
					{ title: "Billing", href: "/team/billing" },
				]}
				title="Billing"
				description="Manage your team subscription and billing"
				icon={
					<IconCurrencyDollar className="size-6 text-primary" aria-hidden />
				}
			/>

			<BillingContent isAnnual={isAnnual} setIsAnnual={setIsAnnual} />

			{!isSubscribed && selectedPlan && checkoutTiers.length > 0 && (
				<PlanCheckoutConfigurator
					plan={selectedPlan}
					isAnnual={isAnnual}
					addonTiers={checkoutTiers}
					addonIndex={addonIndex}
					onAddonIndexChange={setAddonIndex}
					onCheckout={() => void handleContinueCheckout()}
					checkoutLabel={
						isBillingFreeTrialEnabled() ? "Start free trial" : "Checkout"
					}
					checkoutDisabled={!billingUpdate || !selectedProductId}
					isCheckoutLoading={isCheckoutLoading}
					sliderDisabled={!billingUpdate}
				/>
			)}

			{apiPricingEnabled &&
				isSubscribed &&
				activePlan &&
				team.billing.plan_name !== "Free" &&
				checkoutTiers.length > 0 && (
					<PlanCheckoutConfigurator
						plan={activePlan}
						isAnnual={subscriptionIsAnnual}
						addonTiers={checkoutTiers}
						addonIndex={manageAddonIndex}
						onAddonIndexChange={setManageAddonIndex}
						mode="subscription"
						onCheckout={() => void handleApplyManagedAddon()}
						checkoutLabel={managedAddonApplyLabel(
							manageAddonIndex,
							currentAddonQueries,
						)}
						checkoutDisabled={
							!billingUpdate ||
							managedAddonUnchanged ||
							(manageAddonIndex <= 0 && currentAddonQueries === 0)
						}
						isCheckoutLoading={isAddonUpdating}
						sliderDisabled={!billingUpdate}
					/>
				)}

			{team?.billing?.subscription_status === "trialing" &&
				billingDetails?.subscription_period_end && (
					<p className="text-sm text-muted-foreground mt-4">
						{isBillingFreeTrialEnabled()
							? `Your trial ends on ${new Date(billingDetails.subscription_period_end).toLocaleDateString()}. Add a payment method before then to keep your plan.`
							: `Your trial ends on ${new Date(billingDetails.subscription_period_end).toLocaleDateString()}.`}
					</p>
				)}

			{team?.billing?.is_active && billingDetails ? (
				<Card className="rounded-lg border shadow-sm mt-8 lg:mt-12">
					<CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-2 md:space-y-0">
						<div>
							<CardTitle>Manage Subscription</CardTitle>
							<CardDescription>
								View your current plan, update payment methods, or cancel your
								subscription.
							</CardDescription>
						</div>
						<Button
							variant="outline"
							disabled={!billingUpdate}
							onClick={() => void openBillingPortal()}
						>
							Manage Subscription
						</Button>
					</CardHeader>
					{team?.billing.is_active && billingDetails && (
						<CardContent className="space-y-3">
							{billingDetails.subscription_cancel_at_period_end &&
								billingDetails.subscription_period_end && (
									<p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
										{subscriptionCancellationNotice(
											billingDetails.subscription_period_end,
										)}
									</p>
								)}
							<p>
								Current Plan:{" "}
								<span className="font-semibold">
									{getPlanByProductId(team.billing.stripe_product_id)?.name} (
									{team.billing.stripe_price_id ===
									getPlanByProductId(team.billing.stripe_product_id)
										?.yearlyPrice?.id
										? "Yearly"
										: "Monthly"}
									)
								</span>
							</p>
							{billingDetails.subscription_period_end && (
								<p>
									{subscriptionPeriodEndLabel(
										billingDetails.subscription_cancel_at_period_end,
									)}
									:{" "}
									<span className="font-semibold">
										{new Date(
											billingDetails.subscription_period_end,
										).toLocaleDateString()}
									</span>
								</p>
							)}
						</CardContent>
					)}
					<CardFooter className="text-sm text-muted-foreground italic pt-2 border-t mt-4 flex flex-wrap">
						{billingDetails?.subscription_cancel_at_period_end ? (
							<span>
								Changed your mind? Reactivate anytime from Manage Subscription
								before your access ends.
							</span>
						) : (
							<>
								<span>You can cancel your subscription anytime.</span>
								<span className="w-full sm:w-auto sm:ml-1">
									Not satisfied? Contact{" "}
									<a
										href={`mailto:${config.supportEmail}`}
										className="text-primary hover:underline"
									>
										{config.supportEmail}
									</a>{" "}
									for a refund.
								</span>
							</>
						)}
					</CardFooter>
				</Card>
			) : null}

			<InvoiceHistoryCard teamId={teamID} isSubscribed={isSubscribed} />

			<BillingFaqs />
		</PageLayout>
	);
}
